import { Input } from '@ui/components';

import { TableTemplate } from '@repo/types';
import type { Table } from '@tanstack/react-table';
import { Button } from '@ui/components';
import { cn } from '@ui/lib/utils';
import { FileUpIcon } from 'lucide-react';
import React, { useCallback, useRef } from 'react';
import { DeleteButton } from './ui/delete-button';
import { ExportButton } from './ui/export-button';
import { NewButton } from './ui/new-button';
import { ViewButton } from './ui/view-button';

interface DataTableToolbarProps<TData>
  extends React.HTMLAttributes<HTMLDivElement> {
  table: Table<TData>;
  selectedTemplateId?: number | null;
  templates?: TableTemplate[];
  onSelectTemplate?: (template: TableTemplate) => void;
  onDeleteTemplate?: (template: TableTemplate) => void;
  onExcel?: (columns: string[], rows: Partial<TData>[]) => void;
  onPdf?: (rows: string[]) => void;
  onAddNew?: () => void;
  onDelete?: () => void;
  ToolbarRight?: React.ReactNode;
  disableSearch?: boolean;
  onSearch?: (value: string) => void;
  searchValue?: string;
  CustomFilter?: React.ReactNode;
  onImport?: (file: File) => void;
}

/**
 * @deprecated This component is deprecated and will be removed in a future version.
 * Please use AppTable component instead.
 * @see src/components/app-table
 */

export function DataTableToolbar<TData>({
  onDeleteTemplate,
  onSelectTemplate,
  selectedTemplateId,
  templates,
  table,
  className,
  onExcel,
  onPdf,
  onAddNew,
  onDelete,
  ToolbarRight,
  disableSearch,
  onSearch,
  searchValue = '',
  onImport,
  CustomFilter,
}: DataTableToolbarProps<TData>) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!onSearch) {
        return;
      }
      onSearch(String(event.target.value));
      table.setPageIndex(0);
    },
    [onSearch],
  );

  const handleExportExcel = useCallback(() => {
    const columns = table
      .getVisibleFlatColumns()
      .filter((el) => el.id !== 'actions' && el.id !== 'select')
      .map((el) => el.id);
    const rows = table.getRowModel().rows.map((el) =>
      el.getVisibleCells().reduce(
        (acc, el) => {
          acc[el.column.id] = el.getValue();
          return acc;
        },
        {} as Record<string, unknown>,
      ),
    );
    if (onExcel) {
      onExcel(columns, rows as Partial<TData>[]);
    }
  }, [onExcel]);

  const handleExportPdf = useCallback(() => {
    const rows = table
      .getVisibleFlatColumns()
      .filter((el) => el.id !== 'actions' && el.id !== 'select')
      .map((el) => el.id);
    if (onPdf) {
      onPdf(rows);
    }
  }, [onPdf]);

  const handleImport = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file && onImport) {
        onImport(file);
      }
    },
    [onImport],
  );

  return (
    <div
      className={cn(
        'flex flex-wrap flex-row justify-between gap-2 p-4',
        className,
      )}
    >
      <div className="flex flex-row flex-wrap gap-2">
        {!!onAddNew && <NewButton onAddNew={onAddNew} />}

        {!!onImport && (
          <>
            <Input
              ref={inputRef}
              className="hidden"
              type="file"
              onChange={handleImport}
            />
            <Button
              type="button"
              variant="default"
              onClick={() => inputRef.current?.click()}
            >
              <FileUpIcon className="mr-2 size-4" />
              Import
            </Button>
          </>
        )}

        {!disableSearch && (
          <Input
            className="h-10 w-[200px]"
            placeholder="Search..."
            value={searchValue ?? ''}
            onChange={handleSearchChange}
          />
        )}

        {!!CustomFilter && CustomFilter}
      </div>

      <div className="flex flex-row flex-wrap gap-2">
        {!!ToolbarRight && ToolbarRight}

        {!!(onExcel || onPdf) && (
          <ExportButton
            onExcel={onExcel ? handleExportExcel : undefined}
            onPdf={onPdf ? handleExportPdf : undefined}
          />
        )}

        {!!onDelete && <DeleteButton onClick={onDelete} />}

        <ViewButton
          selectedTemplateId={selectedTemplateId}
          table={table}
          templates={templates}
          onDeleteTemplate={onDeleteTemplate}
          onSelectTemplate={onSelectTemplate}
        />
      </div>
    </div>
  );
}
